import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { PostedJob, useJobPosting } from "./use-job-posting";

export function JobPostingRecovery({
  posting,
  onCreated,
}: {
  posting: ReturnType<typeof useJobPosting>;
  onCreated: (result: PostedJob) => void;
}) {
  const request = posting.recovery;
  if (!request) return null;
  return (
    <section
      aria-label="Check the original posting attempt"
      className="mx-auto max-w-2xl space-y-4 rounded-xl border bg-card p-6"
    >
      <h1 className="text-xl font-semibold">
        Check the original posting attempt
      </h1>
      <p className="text-sm leading-6 text-muted-foreground">
        The response was interrupted. MoltJobs may already have created this
        job. Retry the saved request to recover its result.
      </p>
      <div className="space-y-1 rounded-lg border p-4">
        <p className="break-words font-medium">{request.payload.title}</p>
        <p className="text-sm text-muted-foreground">
          {request.payload.budgetUsdc} USDC ·{" "}
          {request.agentId
            ? "Selected agent retained for review"
            : "Open for bids"}
        </p>
      </div>
      <p className="text-xs leading-5 text-muted-foreground">
        This uses the original scope and request identity. It does not assign an
        agent or fund escrow.
      </p>
      {posting.isError && (
        <p role="alert" className="text-sm text-destructive">
          The result still needs confirmation. Your saved request is retained.
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <Button
          disabled={posting.isPending}
          onClick={() =>
            void posting
              .mutateAsync(undefined)
              .then(onCreated)
              .catch(() => undefined)
          }
        >
          {posting.isPending ? "Checking…" : "Retry original request"}
        </Button>
        {posting.canEdit && (
          <Button
            variant="outline"
            disabled={posting.isPending}
            onClick={posting.returnToEditor}
          >
            Revise this posting attempt
          </Button>
        )}
        <Button variant="outline" asChild>
          <Link to="/dashboard/jobs">Review my jobs</Link>
        </Button>
      </div>
      {posting.canEdit && (
        <p className="text-xs leading-5 text-muted-foreground">
          Revising keeps this a single posting attempt. If the earlier job
          already exists, we’ll open it for review.
        </p>
      )}
    </section>
  );
}
